<template>
    <dropdown
        ref="dropdown"
        :position="position"
        :icon="icon ? icon : ''"
        :class="classes"
        :tooltip="`Voices`"
        :tooltip-class="'voices__tooltip-icon'"
        iconType="vector"
        class="voices"
        :text="voiceName"
        keep-open-after-select
        @icon-click-after="iconClickAfter"
    >
        <div class="tabs">
            <ul>
                <li :class="{ 'is-active': currentTab === 'basic' }">
                    <a class="voices__tab" @click.prevent="switchTab('basic')">
                        Basic <span class="counter">{{ basicVoicesCount || 0 }}</span>
                    </a>
                </li>
                <li class="voices__centerTab" :class="{ 'is-active': currentTab === 'premium' }">
                    <a class="voices__tab" @click.prevent="switchTab('premium')">
                        Premium <span class="counter">{{ premiumVoicesCount || 0 }}</span>
                    </a>
                </li>
                <li :class="{ 'is-active': currentTab === 'custom-voices' }">
                    <a class="voices__tab" @click.prevent="switchTab('custom-voices')">
                        Custom Voices <span class="counter">{{ customVoicesCount || 0 }}</span>
                    </a>
                </li>
            </ul>
        </div>
        <basic-loader v-if="currentTab !== 'basic' && voicesLoading" center aria-label="Voices" />
        <template v-else>
            <div v-if="showBetaTag" class="voices__beta">
                <div class="beta-tag">BETA</div>
            </div>
            <div v-if="showSearch" class="voices__inputBox">
                <input v-model="searchText" type="text" placeholder="Search..." class="input" />
            </div>
            <div class="voices__list">
                <template v-if="currentTab === 'basic'">
                    <voices-item
                        v-for="voiceType in searchedVoices(voicesBasic)"
                        :key="voiceType.id"
                        type="basic"
                        :voice="voiceType"
                        :onLoadSampleUrl="handleLoadSampleUrl"
                        @selected-voice="handleSelectedVoice"
                    />
                    <div v-if="!searchedVoices(voicesBasic || []).length" class="voices__notFound">
                        No voice found matching your search.
                    </div>
                </template>
                <template v-if="currentTab === 'premium'">
                    <voices-item
                        v-for="voiceType in searchedVoices(voicesPremium)"
                        :key="voiceType.id"
                        type="premium"
                        :voice="voiceType"
                        :onLoadSampleUrl="handleLoadSampleUrl"
                        @selected-voice="handleSelectedVoice"
                    />
                    <div
                        v-if="!searchedVoices(voicesPremium || []).length"
                        class="voices__notFound"
                    >
                        No voice found matching your search.
                    </div>
                </template>
                <template v-if="currentTab === 'custom-voices'">
                    <template v-if="customVoicesCount">
                        <voices-item
                            v-for="voiceType in voicesCustom"
                            :key="voiceType.id"
                            type="custom"
                            :voice="voiceType"
                            :isCustomVoices="true"
                            :onLoadSampleUrl="handleLoadSampleUrl"
                            @selected-voice="handleSelectedVoice"
                            @remove-voice="handleRemoveVoice"
                        />
                    </template>
                    <template v-if="isShowAddCustomVoiceButton">
                        <div class="voices__button">
                            <a
                                class="create"
                                :class="{ disabled: isAddCustomVoiceButtonDisabled }"
                                @click.prevent="showCreateCustomVoiceModal"
                            >
                                Add a custom voice
                            </a>
                        </div>
                    </template>
                </template>
            </div>
        </template>
    </dropdown>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import VoicesItem from './Voices/VoicesItem';

export default {
    emits: ['showCustomVoiceModal', 'removeCustomVoice'],

    components: {
        VoicesItem,
    },

    props: {
        position: { type: String, default: 'left' },
        icon: { type: [String, Boolean], default: false },
        voiceName: { type: String, default: 'Click to select' },
    },

    data() {
        return {
            currentTab: 'basic',
            searchText: '',
        };
    },

    computed: {
        showSearch() {
            return !(
                this.currentTab === 'custom-voices' ||
                (this.currentTab === 'premium' && this.premiumVoicesCount === 0) ||
                (this.currentPage === 'basic' && this.basicVoicesCount === 0)
            );
        },

        showBetaTag() {
            const isPremium = this.currentTab === 'premium';
            const isCustom = this.currentTab === 'custom-voices';

            return isPremium || isCustom;
        },

        searchOnTop() {
            return this.position.indexOf('bottom') !== -1;
        },

        classes() {
            return {
                voices: true,
                'has-results': !this.voicesLoading && this.displayedResults !== null,
            };
        },

        basicVoicesCount() {
            return this.voicesBasic?.length;
        },

        premiumVoicesCount() {
            return this.voicesPremium?.length;
        },

        customVoicesCount() {
            return this.voicesCustom?.length;
        },

        isShowAddCustomVoiceButton() {
            return (
                (this.customVoicesCount > 0 && !this.isAddCustomVoiceButtonDisabled) ||
                this.customVoicesCount === 0
            );
        },

        isAddCustomVoiceButtonDisabled() {
            return this.availableNumberCustomVoices < 1;
        },

        ...mapState(stores.User, ['user']),
        ...mapState(stores.VoiceAssistant, [
            'voicesLoading',
            'voiceType',
            'voicesBasic',
            'voicesPremium',
            'voicesCustom',
            'availableNumberCustomVoices',
        ]),
    },

    watch: {
        voicesCustom(newValue, oldValue) {
            if (oldValue !== null) {
                this.$store.dispatch('getVoicesStats');
            }
        },
    },

    methods: {
        searchedVoices(voices) {
            const search = this.searchText.trim().toLowerCase();
            if (!search) {
                return voices;
            }

            return voices.filter(({ name }) => name.toLowerCase().includes(search));
        },
        iconClickAfter(e) {
            if (e) e.stopPropagation();
            this.initList();
        },

        async initBasicList() {
            if (!this.voicesBasic) {
                await this.$store.dispatch('getTypeList', 'basic');
            }
            if (!this.voiceType && this.voicesBasic) {
                await this.$store.dispatch('setVoice', { data: this.voicesBasic[0] });
            }
        },

        async initList() {
            await this.initBasicList();

            if (!this.voicesPremium) {
                this.$store.dispatch('getTypeList', 'premium');
            }
            if (!this.voicesCustom) {
                this.$store.dispatch('getTypeList', 'custom');
                this.$store.dispatch('getVoicesStats');
            }
        },

        async handleSelectedVoice(item) {
            this.$store.dispatch('setVoice', { data: item });

            await this.$delay(50);
            await this.$store.dispatch('dropdownClose');
        },

        async switchTab(tab) {
            this.currentTab = tab;
            this.searchText = '';
        },

        showCreateCustomVoiceModal() {
            if (this.isAddCustomVoiceButtonDisabled) return;
            this.$emit('showCustomVoiceModal', true);
        },

        async handleLoadSampleUrl(id, type) {
            return this.$store.dispatch('getVoiceSampleUrl', { id, type });
        },

        handleRemoveVoice(voice) {
            this.$emit('removeCustomVoice', voice);
        },
    },

    mounted() {
        this.initBasicList();
    },
};
</script>
